import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { PortalAuthService } from '../auth/portal-auth.service';
import { environment } from '@env/environment';
import { VerticalWorksAccessResponse } from '../models';

export interface UserRequest {
  userRequestId: string;
  userEmail: string;
  userName: string;
  reason: string;
  requestType: string;
  lastAction?: string;
  createdDate?: string;
  lastModifiedDate?: string;
}

export interface PaginationResult<T> {
  data: T[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
}

export interface AccessRequestStatus {
  hasRequested: boolean;
  status?: string; // 'Pending', 'Unlocked', 'Denied', etc.
  request?: UserRequest;
}

// Partner Portal interfaces
export interface PartnerPortalAccessRequest {
  userName: string;
  userShortName: string;
  reason: string | null;
  portfolioName: string;
}

// Vertical Works access contract — declared once in core/models and shared by
// GET /access/status (app-roles.service.ts) and POST /access/request below,
// which return the same body shape. Imported here rather than redeclared: the
// two calls used to carry two shapes, and the one with the real status union
// was not the one the status call used.

// Online Colo user record (subset of OC User model — only fields we send/read)
export interface ColoUser {
  id?: string;
  userEmail: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  company?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phoneNumber?: string;
  describesAffiliationWithVB?: string;
  describesRoleInCompany?: string;
  isInternal?: boolean;
  isRegistrationComplete?: boolean;
  roles?: string[];
  contacts?: unknown[];
  radEquipments?: unknown[];
  radLines?: unknown[];
}

@Injectable({
  providedIn: 'root'
})
export class RequestAccessService {
  private http = inject(HttpClient);
  private authService = inject(PortalAuthService);

  private readonly onlineColoApiUrl = environment.onlineColoApiUrl || 'https://devcolo.verticalbridge.com/api';
  private readonly partnerPortalApiUrl = environment.partnerPortalApiUrl || 'https://app-vb-pp-dev-eus.azurewebsites.net/api';
  private readonly verticalIntelApiUrl = environment.verticalIntelApiUrl || 'https://devverticalintel.verticalbridge.com/api';
  private readonly verticalWorksApiUrl = environment.verticalWorksApiUrl;

  checkExistingRequest(userEmail: string): Observable<AccessRequestStatus> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.authService.getToken()}`
    });

    const params = new HttpParams()
      .set('pageNumber', '0')
      .set('pageSize', '1')
      .set('filterColumnName', 'userEmail')
      .set('filterValue', userEmail);

    return this.http.get<PaginationResult<UserRequest>>(
      `${this.onlineColoApiUrl}/UserRequestUnlockSiteLocator/GetPagedList`,
      { headers, params }
    ).pipe(
      map(result => {
        if (result.totalCount > 0) {
          const request = result.data?.[0];
          return {
            hasRequested: true,
            status: request?.lastAction || 'Pending',
            request: request
          };
        }
        return { hasRequested: false };
      }),
      catchError(() => {
        return of({ hasRequested: false });
      })
    );
  }

  requestSiteLocatorAccess(userName: string, userEmail: string, reason: string): Observable<UserRequest> {
    const request: UserRequest = {
      userRequestId: this.generateGuid(),
      userEmail: userEmail,
      userName: userName,
      reason: reason,
      requestType: 'SiteLocator',
      createdDate: new Date().toISOString()
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.authService.getToken()}`
    });

    return this.http.post<UserRequest>(
      `${this.onlineColoApiUrl}/UserRequestUnlockSiteLocator/RequestUnlockSiteLocator`,
      request,
      { headers }
    );
  }

  // Partner Portal methods
  requestPartnerPortalAccess(userName: string, userShortName: string, reason: string, portfolioName: string): Observable<unknown> {
    const request: PartnerPortalAccessRequest = {
      userName: userName,
      userShortName: userShortName,
      reason: reason,
      portfolioName: portfolioName
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json-patch+json',
      'Authorization': `Bearer ${this.authService.getToken()}`
    });

    return this.http.post<unknown>(
      `${this.partnerPortalApiUrl}/AccessRequest/submit`,
      request,
      { headers }
    );
  }

  // Vertical Intel methods (Colo-style endpoint pattern)
  requestVerticalIntelAccess(userName: string, userEmail: string, reason: string): Observable<UserRequest> {
    const request: UserRequest = {
      userRequestId: this.generateGuid(),
      userEmail: userEmail,
      userName: userName,
      reason: reason,
      requestType: 'VerticalIntel',
      createdDate: new Date().toISOString()
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.authService.getToken()}`
    });

    return this.http.post<UserRequest>(
      `${this.verticalIntelApiUrl}/UserRequest/RequestAccess`,
      request,
      { headers }
    );
  }

  /**
   * Vertical Works access request. Anonymous and rate limited on that side —
   * no Authorization header — and it takes a company name rather than a
   * reason: the name is what its approvals queue shows for the pending vendor
   * firm. A 403 comes back for @verticalbridge.com addresses, since internal
   * access there is configuration rather than a request; the tile never
   * offers this path to those users, so a 403 means the branch has drifted.
   *
   * The request grants access immediately (approval gates jobs inside
   * Vertical Works, not the handoff), so callers should refresh roles rather
   * than showing a pending badge.
   */
  requestVerticalWorksAccess(
    userEmail: string,
    userName: string,
    companyName: string
  ): Observable<VerticalWorksAccessResponse> {
    // Guards the empty-URL case the status call in app-roles.service.ts
    // already guards, so the two halves of one contract agree. Unlike the
    // other three API URLs this one has no `||` fallback (falling back to a
    // dev URL from prod would be worse), so with it empty the template
    // literal below is the *relative* URL `/access/request`. This repo
    // registers no HTTP interceptors, so HttpClient would resolve that
    // against the Portal's own origin and POST the user's name, email and
    // company to a 404 — surfacing in the modal as "Failed to submit
    // request. Please try again." and an indefinite retry loop.
    if (!this.verticalWorksApiUrl) {
      return throwError(
        () => new Error('Vertical Works is not available in this environment.')
      );
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<VerticalWorksAccessResponse>(
      `${this.verticalWorksApiUrl}/access/request`,
      { email: userEmail, name: userName, companyName },
      { headers }
    );
  }

  // Check existing VI access request status
  checkExistingViRequest(userEmail: string): Observable<AccessRequestStatus> {
    // Vertical Intel is not deployed in production yet; the endpoint is
    // unreachable there and the request would hang. The VI tile is also
    // hidden in prod, so there's nothing to populate — report "no request".
    if (environment.production) {
      return of({ hasRequested: false });
    }

    const encodedEmail = encodeURIComponent(userEmail);

    return this.http.get<{ status: string; requestId?: string; requestedAt?: string }>(
      `${this.verticalIntelApiUrl}/AccessRequest/status?userEmail=${encodedEmail}`
    ).pipe(
      map(result => ({
        hasRequested: true,
        status: result.status || 'Pending'
      })),
      catchError(() => {
        // 404 or other errors = no existing request
        return of({ hasRequested: false });
      })
    );
  }

  // Check Vertical Intel access directly from backend (same endpoint Auth0 action uses)
  checkVerticalIntelAccess(userEmail: string): Observable<{ hasAccess: boolean; roles: string[]; userId?: string }> {
    const encodedEmail = encodeURIComponent(userEmail);

    return this.http.get<{ roles?: string[]; userId?: string }>(
      `${this.verticalIntelApiUrl}/user/GetUser?userEmail=${encodedEmail}`
    ).pipe(
      map(response => ({
        hasAccess: true,
        roles: response?.roles || [],
        userId: response?.userId
      })),
      catchError(() => of({ hasAccess: false, roles: [] }))
    );
  }

  /**
   * Read the Colo user record for the currently-authenticated user.
   * Returns null if the user has no record yet (triggers registration flow).
   * For @verticalbridge.com emails, the Colo backend auto-creates the record
   * with admin-read + sitelocator-access roles and returns it.
   *
   * Distinguishes "no record" (404 → null) from transient backend errors
   * (5xx → re-thrown). Without this distinction, a transient 500 would
   * look like "no record" and trigger a spurious CreateUser write.
   */
  getColoUserData(): Observable<ColoUser | null> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.getToken()}`,
    });
    return this.http
      .get<ColoUser | null>(`${this.onlineColoApiUrl}/User/GetUserData`, {
        headers,
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            return of(null);
          }
          // 5xx and other failures: bubble up so callers don't mistake
          // a transient outage for "no record exists".
          return throwError(() => err);
        })
      );
  }

  /**
   * Create the Colo user record for a non-VB user after Portal sign-up.
   * The backend's UserService auto-assigns the user-external role when no
   * roles are provided and the email is non-VB. Caller should follow this
   * with PortalAuthService.refreshToken() so the new role lands in the
   * current session's access token.
   */
  createColoUser(user: ColoUser): Observable<ColoUser> {
    const userDto = { user, userEmail: user.userEmail };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.authService.getToken()}`,
    });
    return this.http.post<ColoUser>(
      `${this.onlineColoApiUrl}/User/CreateUser`,
      userDto,
      { headers }
    );
  }

  /**
   * Update the Colo user record for the currently-authenticated user.
   * Online Colo owns the user record, so account edits from Portal PUT
   * straight to the same endpoint the Colo "My Profile" page uses. Callers
   * should send the full record (merge edits onto the value returned by
   * getColoUserData) so unrelated fields — roles, isInternal, contacts —
   * aren't wiped.
   */
  updateColoUser(user: ColoUser): Observable<ColoUser> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.authService.getToken()}`,
    });
    return this.http.put<ColoUser>(
      `${this.onlineColoApiUrl}/User/UpdateUser`,
      user,
      { headers }
    );
  }

  ensureColoUser(user: ColoUser): Observable<ColoUser> {
    const userDto = { user, userEmail: user.userEmail };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.authService.getToken()}`,
    });
    return this.http.post<ColoUser>(
      `${this.onlineColoApiUrl}/User/EnsureUser`,
      userDto,
      { headers }
    );
  }

  buildColoHandoffUrl(): string {
    const token = this.authService.getToken();
    const baseUrl = environment.onlineColoUrl;
    return token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
  }

  /**
   * Full Colo user provisioning chain used by signup, direct login, and
   * social-auth callback. Consolidated here so the three call-sites can't
   * drift apart over time.
   *
   * 1. GetUserData — for @verticalbridge.com emails the backend
   *    auto-creates the record with admin-read + sitelocator-access;
   *    for non-VB emails returns null when no record exists.
   * 2. If null, CreateUser with a minimal record (backend assigns
   *    user-external for non-VB).
   * 3. refreshToken so any newly-assigned role claim lands in the
   *    current session's access token (no need to re-login).
   *
   * On HTTP failure anywhere in the chain the user gets a null and the
   * caller can decide whether to navigate / show a message. A future
   * follow-up could log non-404 failures to App Insights.
   */
  ensureColoUserProvisioned(
    userEmail: string,
    firstName?: string,
    lastName?: string
  ): Observable<ColoUser | null> {
    return this.getColoUserData().pipe(
      switchMap(existing => {
        if (existing) {
          return of(existing);
        }
        const newUser: ColoUser = {
          id: userEmail,
          userEmail,
          firstName,
          lastName,
          isInternal: false,
          contacts: [],
          radEquipments: [],
          radLines: [],
        };
        return this.createColoUser(newUser);
      }),
      switchMap(user => this.authService.refreshToken().pipe(map(() => user))),
      catchError(() => of(null))
    );
  }

  /**
   * Notify the Site Locator access-request recipient list (via Online Colo's
   * PortalNotification endpoint) that a new user signed up for VB Portal and
   * gained access to Online Colo. Sent on signup only.
   */
  notifyPortalSignup(userEmail: string, userName?: string): Observable<boolean> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.authService.getToken()}`
    });

    return this.http.post<boolean>(
      `${this.onlineColoApiUrl}/PortalNotification/NotifySignup`,
      { userEmail, userName },
      { headers }
    );
  }

  // Check Online Colo access directly from backend (same endpoint Auth0 action uses)
  checkOnlineColoAccess(userEmail: string): Observable<{ hasAccess: boolean; roles: string[]; userId?: string }> {
    const encodedEmail = encodeURIComponent(userEmail);

    return this.http.get<{ roles?: string[]; userId?: string }>(
      `${this.onlineColoApiUrl}/user/GetUser?userEmail=${encodedEmail}`
    ).pipe(
      map(response => ({
        hasAccess: true,
        roles: response?.roles || [],
        userId: response?.userId
      })),
      catchError(() => of({ hasAccess: false, roles: [] }))
    );
  }

  getColoApplicationCount(userEmail: string): Observable<number> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.authService.getToken()}`,
    });
    const params = new HttpParams()
      .set('pageIndex', '0')
      .set('pageSize', '1')
      .set('applicationUser', userEmail);
    return this.http
      .get<{ totalCount?: number }>(`${this.onlineColoApiUrl}/Application/GetPagedList`, { headers, params })
      .pipe(
        map(result => result?.totalCount ?? 0),
        catchError(() => of(0)),
      );
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
